import { getGoogleAdsSnapshot, getTrackingMetadataSnapshot, mergeGoogleAdsUrlTracking } from './googleAdsTracking.js';
import { getSeoRoute } from '../seo/siteRoutes.js';
import { gateOpenAIAdsHandoff, getOpenAIAdsContext } from './openaiAdsTracking.js';

const BUSINESS_PHONE = '972503969640';
const REFERENCE_PATTERN = /^PTW-[0-9a-f]{32}$/;
const CLICK_EVENT_PATTERN = /^wc_(?:[0-9a-f]{32}|[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12})$/;
const CONSENT_FIELDS = ['ad_storage', 'analytics_storage', 'ad_user_data', 'ad_personalization'];
const CONSENT_VALUES = new Set(['granted', 'denied', 'unknown']);
const MAX_BODY_BYTES = 32 * 1024;
const MAX_LINK_LENGTH = 8192;
const MAX_CANDIDATES = 20;
const stagedAnchors = new WeakMap();
const DEVICES = new Set(['mobile', 'tablet', 'desktop']);
export const WHATSAPP_SEARCH_ENTRY_SURFACE = 'search_sitelink_entry';
export const WHATSAPP_SEARCH_ENTRY_SOURCE = 'google_ads_sitelink';

export const WHATSAPP_SURFACES = new Set([
  'header_mobile',
  'header_desktop',
  'widget_inline',
  'widget_floating',
  'home_final_cta',
  'contact',
  'faq',
  'works',
]);

export function isWhatsAppEnquiryAttributionEnabled(value = import.meta.env?.VITE_WHATSAPP_ENQUIRY_ATTRIBUTION_ENABLED) {
  return value === 'true';
}

export function createWhatsAppClickEventId() {
  try {
    const uuid = globalThis.crypto?.randomUUID?.();
    if (typeof uuid === 'string' && CLICK_EVENT_PATTERN.test(`wc_${uuid}`)) return `wc_${uuid}`;
  } catch (_) {}
  return `wc_${Array.from({ length: 4 }, () => Math.floor(Math.random() * 0x100000000))
    .map((part) => part.toString(16).padStart(8, '0')).join('')}`;
}

export function createWhatsAppEnquiryRef(cryptoProvider = globalThis.crypto) {
  try {
    if (typeof cryptoProvider?.getRandomValues === 'function') {
      const bytes = cryptoProvider.getRandomValues(new Uint8Array(16));
      return `PTW-${Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('')}`;
    }
  } catch (_) {}
  try {
    const uuid = cryptoProvider?.randomUUID?.();
    if (typeof uuid === 'string'
      && /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(uuid)) {
      return `PTW-${uuid.replaceAll('-', '').toLowerCase()}`;
    }
  } catch (_) {}
  // A weak reference could join unrelated customers. The original link still works.
  return null;
}

export function buildWhatsAppEnquiryHref(href, enquiryRef) {
  if (typeof href !== 'string' || href.length > MAX_LINK_LENGTH
    || !REFERENCE_PATTERN.test(enquiryRef) || /[\u0000-\u0020\u007f]/.test(href)) return null;
  try {
    const url = new URL(href);
    if (url.protocol !== 'https:' || url.username || url.password || url.port || url.hash) return null;
    const isShortLink = url.hostname === 'wa.me' && url.pathname === `/${BUSINESS_PHONE}`;
    const isApiLink = url.hostname === 'api.whatsapp.com' && url.pathname === '/send'
      && url.searchParams.getAll('phone').length === 1
      && url.searchParams.get('phone') === BUSINESS_PHONE;
    if (!isShortLink && !isApiLink) return null;
    if (url.searchParams.getAll('text').length > 1) return null;
    // A conflicting phone parameter is not an unambiguous business destination.
    if (isShortLink && url.searchParams.has('phone')
      && (url.searchParams.getAll('phone').length !== 1
        || url.searchParams.get('phone') !== BUSINESS_PHONE)) return null;
    // URLSearchParams replaces malformed escapes; do not silently alter such messages.
    for (const pair of url.search.slice(1).split('&')) {
      for (const part of pair.split('=')) decodeURIComponent(part.replaceAll('+', ' '));
    }
    const message = url.searchParams.get('text') || '';
    if (/PTW-[0-9a-f]{32}/i.test(message)) return null;
    // Keep two blank lines between the original customer text and its matching reference.
    url.searchParams.set('text', `${message}${message ? '\n\n\n' : ''}מספר פנייה: ${enquiryRef}`);
    const enriched = url.toString();
    return enriched.length <= MAX_LINK_LENGTH ? enriched : null;
  } catch (_) {
    return null;
  }
}

function unknownConsent() {
  return Object.fromEntries(CONSENT_FIELDS.map((field) => [field, 'unknown']));
}

function normalizeConsent(input) {
  const consent = unknownConsent();
  for (const field of CONSENT_FIELDS) {
    if (CONSENT_VALUES.has(input?.[field])) consent[field] = input[field];
  }
  return consent;
}

// Observation only: this never changes Google consent or treats missing signals as consent.
export function readWhatsAppConsent(dataLayer) {
  const consent = unknownConsent();
  if (!Array.isArray(dataLayer)) return consent;
  const updated = new Set();
  const regional = new Set();
  for (const command of dataLayer) {
    if (!command || typeof command !== 'object' || command[0] !== 'consent'
      || !['default', 'update'].includes(command[1])) continue;
    const parameters = command[2];
    if (!parameters || typeof parameters !== 'object' || Array.isArray(parameters)) continue;
    for (const field of CONSENT_FIELDS) {
      const value = parameters[field];
      if (value !== 'granted' && value !== 'denied') continue;
      if (Object.hasOwn(parameters, 'region')) {
        // Region applicability is unknown. Keep a known denial, never a guessed grant.
        regional.add(field);
        if (consent[field] !== 'denied') consent[field] = 'unknown';
      } else if (command[1] === 'update') {
        consent[field] = value;
        updated.add(field);
        regional.delete(field);
      } else if (!updated.has(field)) {
        consent[field] = regional.has(field) && value !== 'denied' ? 'unknown' : value;
      }
    }
  }
  return consent;
}

function boundedString(value, limit) {
  return typeof value === 'string' && value.length <= limit
    && !/[\u0000-\u001f\u007f]/.test(value);
}

function copyString(target, source, key, limit, nullable = false) {
  if (nullable && source[key] === null) target[key] = null;
  else if (boundedString(source[key], limit)) target[key] = source[key];
}

function copyCandidate(source) {
  if (!source || typeof source !== 'object' || Array.isArray(source)
    || !['gclid', 'gbraid', 'wbraid'].includes(source.identifier_type)
    || !boundedString(source.identifier, 512) || !source.identifier) return null;
  const candidate = { identifier_type: source.identifier_type, identifier: source.identifier };
  copyString(candidate, source, 'source', 40);
  copyString(candidate, source, 'captured_at', 40, true);
  copyString(candidate, source, 'observed_at', 40, true);
  copyString(candidate, source, 'trust_state', 40);
  copyString(candidate, source, 'ineligible_reason', 80, true);
  if (source.version === null || (Number.isSafeInteger(source.version) && source.version >= 0)) {
    candidate.version = source.version;
  }
  if (typeof source.eligible === 'boolean') candidate.eligible = source.eligible;
  return candidate;
}

export function buildWhatsAppAttribution(snapshot = {}, consentInput) {
  const source = snapshot && typeof snapshot === 'object' && !Array.isArray(snapshot) ? snapshot : {};
  const consent = normalizeConsent(consentInput);
  const attribution = {};
  for (const [key, limit] of [
    ['gclid', 512], ['gbraid', 512], ['wbraid', 512],
    ['campaign', 256], ['keyword', 256], ['utmSource', 128], ['utmMedium', 128],
    ['lastCapturedAt', 40], ['clickCaptureSource', 40], ['googleClickConflictReason', 80],
  ]) copyString(attribution, source, key, limit);
  if (source.clickCaptureVersion === null
    || (Number.isSafeInteger(source.clickCaptureVersion) && source.clickCaptureVersion >= 0)) {
    attribution.clickCaptureVersion = source.clickCaptureVersion;
  }
  for (const key of ['googleClickIdConflict', 'googleClickCandidateOverflow']) {
    if (typeof source[key] === 'boolean') attribution[key] = source[key];
  }
  if (Array.isArray(source.google_click_candidates)) {
    attribution.google_click_candidates = source.google_click_candidates.slice(0, MAX_CANDIDATES)
      .map(copyCandidate).filter(Boolean);
    if (source.google_click_candidates.length > MAX_CANDIDATES) {
      attribution.googleClickCandidateOverflow = true;
    }
  }
  if (consent.ad_storage === 'denied' || consent.ad_user_data === 'denied') {
    delete attribution.gclid;
    delete attribution.gbraid;
    delete attribution.wbraid;
    delete attribution.google_click_candidates;
  }
  // GA identifiers are optional and are not read or requested by this handoff.
  return attribution;
}

function boundedBody(payload) {
  const body = JSON.stringify(payload);
  const bytes = typeof TextEncoder === 'function'
    ? new TextEncoder().encode(body).byteLength : body.length * 3;
  return bytes <= MAX_BODY_BYTES ? body : null;
}

export function buildWhatsAppEnquiryPayload({
  enquiryRef,
  clickEventId,
  surface,
  pathname,
  attribution,
  openaiAds,
  consent: consentInput,
  entrySource,
  device,
} = {}) {
  const isSearchEntry = surface === WHATSAPP_SEARCH_ENTRY_SURFACE;
  if (!REFERENCE_PATTERN.test(enquiryRef) || !CLICK_EVENT_PATTERN.test(clickEventId)
    || (!WHATSAPP_SURFACES.has(surface) && !isSearchEntry)) return null;
  if (isSearchEntry ? entrySource !== WHATSAPP_SEARCH_ENTRY_SOURCE : entrySource !== undefined) return null;
  const consent = normalizeConsent(consentInput);
  const pagePath = typeof pathname === 'string' && pathname ? getSeoRoute(pathname)?.path : null;
  if (isSearchEntry && pagePath !== '/contact') return null;
  const payload = {
    schemaVersion: 1,
    enquiryRef,
    clickEventId,
    surface,
    pagePath: pagePath || null,
    attribution: buildWhatsAppAttribution(attribution, consent),
    consent,
  };
  const permittedOpenAIAds = gateOpenAIAdsHandoff(openaiAds, openaiAds?.consent);
  if (permittedOpenAIAds) payload.openaiAds = permittedOpenAIAds;
  if (isSearchEntry) payload.entrySource = entrySource;
  if (DEVICES.has(device)) payload.device = device;
  if (boundedBody(payload)) return payload;
  // Optional measurement must not make an otherwise valid inquiry too large.
  delete payload.openaiAds;
  return boundedBody(payload) ? payload : null;
}

export function stageWhatsAppEnquiry(anchor, reservedEnquiryRef) {
  if (!anchor || String(anchor.tagName).toLowerCase() !== 'a'
    || typeof anchor.getAttribute !== 'function' || typeof anchor.setAttribute !== 'function') return null;
  const currentHref = anchor.getAttribute('href');
  const previous = stagedAnchors.get(anchor);
  const originalHref = previous && currentHref === previous.enrichedHref
    ? previous.originalHref : currentHref;
  const enquiryRef = reservedEnquiryRef ?? createWhatsAppEnquiryRef();
  if (!enquiryRef) return null;
  const enrichedHref = buildWhatsAppEnquiryHref(originalHref, enquiryRef);
  if (!enrichedHref) return null;
  const stage = { originalHref, enrichedHref, enquiryRef };
  anchor.setAttribute('href', enrichedHref);
  stagedAnchors.set(anchor, stage);
  return {
    enquiryRef,
    restore() {
      if (stagedAnchors.get(anchor) !== stage) return;
      stagedAnchors.delete(anchor);
      if (anchor.getAttribute('href') === enrichedHref) anchor.setAttribute('href', originalHref);
    },
  };
}

export function captureWhatsAppEnquiryPayload(input, {
  readAttribution = getGoogleAdsSnapshot,
  readMetadata = getTrackingMetadataSnapshot,
  readOpenAIAds = getOpenAIAdsContext,
  dataLayer = typeof window !== 'undefined' ? window.dataLayer : null,
  capturedAt = new Date().toISOString(),
} = {}) {
  let attribution = {};
  let metadata = {};
  let consent = unknownConsent();
  let openaiAds;
  try { attribution = readAttribution(); } catch (_) {}
  try { metadata = readMetadata(); } catch (_) {}
  try { consent = readWhatsAppConsent(dataLayer); } catch (_) {}
  try { openaiAds = readOpenAIAds(); } catch (_) {}
  // Read the arriving URL directly instead of depending on another React effect
  // having already persisted its Google Ads snapshot. This creates no storage write.
  if (input?.surface === WHATSAPP_SEARCH_ENTRY_SURFACE && typeof input.search === 'string') {
    try { attribution = mergeGoogleAdsUrlTracking(attribution, input.search, capturedAt).googleAds; } catch (_) {}
  }
  return buildWhatsAppEnquiryPayload({ ...input, attribution, openaiAds, consent, device: metadata?.device });
}

export function postWhatsAppEnquiry(payload, fetchImpl = typeof window !== 'undefined'
  && typeof window.fetch === 'function' ? window.fetch.bind(window) : null, {
  readOpenAIAds = getOpenAIAdsContext,
} = {}) {
  if (typeof fetchImpl !== 'function') return { queued: false, reason: 'fetch_unavailable' };
  try {
    // The OpenAI handoff is checked at dispatch too, including immutable entry retries.
    let currentOpenAIAds;
    try { currentOpenAIAds = readOpenAIAds(); } catch (_) {}
    let permittedOpenAIAds = gateOpenAIAdsHandoff(payload?.openaiAds, currentOpenAIAds?.consent);
    if (permittedOpenAIAds && permittedOpenAIAds.consent?.ad_storage !== 'denied') {
      // A delayed retry must still refer to the same live attribution episode.
      const tokenField = permittedOpenAIAds.version === 2 ? 'revocation_token' : 'consent_token';
      if (currentOpenAIAds?.version !== permittedOpenAIAds.version
        || currentOpenAIAds?.[tokenField] !== permittedOpenAIAds[tokenField]
        || currentOpenAIAds?.captured_at !== permittedOpenAIAds.captured_at
        || currentOpenAIAds?.oppref !== permittedOpenAIAds.oppref) permittedOpenAIAds = undefined;
    }
    const safePayload = buildWhatsAppEnquiryPayload({
      ...payload, pathname: payload?.pagePath,
      openaiAds: permittedOpenAIAds,
    });
    if (!safePayload) return { queued: false, reason: 'invalid_payload' };
    const result = fetchImpl('/api/whatsapp/enquiry', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      mode: 'same-origin',
      keepalive: true,
      body: boundedBody(safePayload),
    });
    // Provider receipt is not guaranteed. Never delay or retry native navigation.
    Promise.resolve(result).catch(() => {});
    return { queued: true };
  } catch (_) {
    return { queued: false, reason: 'fetch_exception' };
  }
}
